/**
 * English messages.
 *
 * `ui` holds atomic strings reached with `t('ui.…')`.
 * `sections` holds structured page copy reached with `useSection('…')`.
 * Both live in this one file per locale so a translator sees the whole surface
 * at once; see `src/i18n/index.js` for why there are two access paths.
 *
 * Nothing here carries layout, colour, or asset data — those stay in
 * `src/data/media.js` so a translation can never break the design.
 */
export default {
  ui: {
    language: {
      label: 'Language',
      en: 'English',
      id: 'Bahasa Indonesia',
      switchTo: 'Switch to {language}'
    },
    nav: {
      primary: 'Primary',
      footer: 'Footer',
      toggle: 'Toggle navigation',
      skip: 'Skip to content',
      items: {
        platform: 'Platform',
        solutions: 'Solutions',
        modules: 'Modules',
        industries: 'Industries',
        technology: 'Technology',
        whyCustom: 'Why Custom',
        contact: 'Contact',
        requestDemo: 'Request a demo'
      }
    },
    actions: {
      requestDemo: 'Request Demo',
      requestDemoLong: 'Request a Demo',
      exploreOmase: 'Explore OMASE',
      scheduleConsultation: 'Schedule Free Consultation',
      chatWhatsApp: 'Chat via WhatsApp',
      sendEmail: 'Send Email',
      close: 'Close',
      closeDialog: 'Close dialog',
      copy: 'Copy',
      copied: 'Copied',
      copyField: 'Copy {field}',
      newTab: '(opens in a new tab)'
    },
    form: {
      heading: 'Request a demo',
      name: 'Full name',
      email: 'Work email',
      company: 'Company',
      message: 'What would you like to see?',
      optional: '(optional)',
      submit: 'Send request',
      submitting: 'Sending…',
      dismiss: 'Dismiss',
      successTitle: 'Request received.',
      successBody:
        'Our team will contact you within one business day to schedule your OMASE walkthrough.',
      errors: {
        name: 'Please enter your full name.',
        email: 'Please enter a valid work email address.',
        company: 'Please enter your company name.',
        send: 'We could not send your request. Please try again, or email us directly at {email}.',
        notConfigured: 'This form is not configured yet. Please email us directly at {email}.'
      },
      noMessage: '(no message provided)',
      subject: 'OMASE demo request — {company}',
      fromName: 'OMASE Landing Page'
    },
    a11y: {
      whatsapp: 'Chat on WhatsApp at {number}',
      rating: 'Rated 5 out of 5',
      dashboardPreview: 'Dashboard preview',
      implementationProof: 'Implementation proof',
      copiedToClipboard: '{field} copied to clipboard'
    }
  },

  sections: {
    hero: {
      badge: 'One Platform. One Data. One Enterprise.',
      headline: ['Run Your Entire Enterprise', 'From One Intelligent Platform.'],
      lede: 'OMASE connects people, operations, data, workflows, and intelligence into one unified enterprise ecosystem.',
      chain: ['People', 'Operations', 'Safety', 'Procurement', 'Finance', 'Assets', 'Data', 'AI'],
      imageAlt: 'OMASE HSE dashboard displayed on a boardroom screen during a site safety review',
      captions: ['HSE Dashboard', 'Live in production']
    },

    proof: {
      kicker: 'Live in production',
      title: 'Successfully implemented in two mining companies.',
      body: 'Empowering operations through integration, automation, and data excellence — powered by the OMASE engine.',
      tags: ['Secure & Reliable', 'Cloud Based', 'Multi Platform', 'Scalable'],
      imageAlt:
        'Official announcement of the ONEMASE implementation by OMASE, live in two mining companies'
    },

    ecosystem: {
      kicker: 'Enterprise Ecosystem',
      title: 'One Platform. Every Operation.',
      lede: 'Every business function shares one data layer, one identity model, and one workflow engine — so nothing is reconciled twice.',
      modules: [
        { name: 'Human Capital', tag: 'People', description: 'Org structure, contracts, payroll inputs.' },
        { name: 'Attendance', tag: 'People', description: 'Shift, roster, and field time capture.' },
        { name: 'HSE & Safety', tag: 'Safety', description: 'Incidents, permits, inspections, compliance.' },
        { name: 'Procurement', tag: 'Supply', description: 'Request to purchase order to receipt.' },
        { name: 'Asset Management', tag: 'Assets', description: 'Registry, maintenance, utilization.' },
        { name: 'Operations', tag: 'Ops', description: 'Daily production and activity control.' },
        { name: 'Finance', tag: 'Finance', description: 'Budget, cost control, and settlement.' },
        { name: 'KPI & Performance', tag: 'Ops', description: 'Targets cascaded site to enterprise.' },
        { name: 'Workflow & Approval', tag: 'Core', description: 'One engine for every approval path.' },
        { name: 'Document Management', tag: 'Core', description: 'Versioned, controlled, auditable.' },
        { name: 'Analytics', tag: 'Data', description: 'Cross-module reporting on one model.' },
        { name: 'AI Intelligence', tag: 'Data', description: 'Anomaly detection and recommendations.' }
      ]
    },

    differentiators: {
      title: 'Why enterprises standardize on OMASE.',
      values: [
        { title: 'Unified Data', body: 'One source of truth across the enterprise.' },
        { title: 'Connected Workflow', body: 'Connect people, processes, approvals, and operations.' },
        { title: 'Real-Time Visibility', body: 'Monitor enterprise performance from anywhere.' },
        { title: 'Enterprise Scale', body: 'Multi-company, multi-site, multi-department.' },
        { title: 'Offline First', body: 'Designed for remote and field operations.' },
        { title: 'Intelligent Automation', body: 'Turn operational data into automated decisions.' }
      ]
    },

    platform: {
      kicker: 'Platform Experience',
      title: 'Designed for the control room and the field.',
      windowLabel: 'omase.enterprise / operations',
      approvalQueue: 'Approval queue',
      kpis: [
        { label: 'Sites connected', delta: '+6 this quarter' },
        { label: 'Workflow cycle time', delta: '−34% vs baseline' },
        { label: 'Data reconciliation', delta: 'manual steps' },
        { label: 'Field sync rate', delta: 'offline capable' }
      ],
      chart: { title: 'Operational throughput', range: 'Last 12 weeks' },
      approvals: [
        { title: 'PR-4482 · Spare parts', meta: 'Site B · Procurement', state: 'Approved' },
        { title: 'Overtime · Crew 12', meta: 'Site A · Human Capital', state: 'Pending' },
        { title: 'Work permit HSE-119', meta: 'Plant 2 · Safety', state: 'Review' },
        { title: 'Budget shift Q3', meta: 'HQ · Finance', state: 'Approved' }
      ],
      highlights: [
        {
          kicker: 'HSE & Safety',
          title: 'Safety Accountability Program',
          body: 'Inspection, observation, hazard report, and corrective action tracked to closure.',
          imageAlt: 'Safety Accountability Program dashboard used by mining operations teams'
        },
        {
          kicker: 'Human Capital',
          title: 'Integrated HC on mobile',
          body: 'Presence with face verification, leave, business trip, and roster in one app.',
          imageAlt: 'Integrated human capital mobile application screens for presence, leave, and roster'
        }
      ],
      gallery: {
        kicker: 'Dashboards in production',
        title: 'Reports and dashboards teams use every day.',
        tabs: [
          {
            label: 'HSE Dashboard',
            title: 'HSE Dashboard',
            body: 'Incidents, cost, shift distribution, and safety performance analytics across sites.',
            heroAlt: 'OMASE Safety Accountability Program dashboard on a dual-screen workstation',
            shots: [
              {
                alt: 'HSE dashboard on a mine control room screen',
                caption: 'Control room — incident trend and site distribution in real time.'
              },
              {
                alt: 'HSE dashboard on an office workstation',
                caption: 'Office desk — category, location, and cost of incidents.'
              }
            ]
          },
          {
            label: 'HC Dashboard',
            title: 'HC Dashboard & Reports',
            body: 'Headcount, attrition, attendance matrix, and daily workforce activity in one view.',
            heroAlt: 'HCGA workforce dashboard reviewed on a boardroom screen',
            shots: [
              {
                alt: 'HCGA dashboard on a desktop monitor',
                caption: 'Attrition trend, onboarding status, and leave monitoring.'
              },
              {
                alt: 'HCGA dashboard on a laptop',
                caption: 'Attendance matrix and employee activity, laptop-ready.'
              }
            ]
          }
        ]
      },
      surfaces: [
        {
          kicker: 'Dashboard',
          title: 'Enterprise control view',
          body: 'Company, site, and department performance in one hierarchy.'
        },
        {
          kicker: 'Mobile',
          title: 'Field-ready application',
          body: 'Capture attendance, inspections, and permits offline.'
        },
        {
          kicker: 'Workflow',
          title: 'Approval anywhere',
          body: 'Delegation, escalation, and audit trail by default.'
        }
      ]
    },

    presensi: {
      kicker: 'Presensi Mobile',
      title: 'Attendance that works at the gate, the pit, and the office.',
      lede: 'Face verification with liveness check, GPS and site validation, offline capture, and approval that flows straight into Human Capital.',
      gateAlt:
        'Employee checking in with face verification on the OMASE attendance app at a site gate',
      suiteAlt:
        'OMASE attendance app screens for clock in, leave request, face verification, and attendance revision',
      suiteCaption:
        'Clock in, leave and permit submission, face verification, and attendance revision — one app for every worker.',
      features: [
        {
          kicker: 'Identity',
          title: 'Face verification',
          body: 'Liveness check prevents buddy punching at every check-in.'
        },
        {
          kicker: 'Location',
          title: 'Geofence by site',
          body: 'GPS accuracy and site area validated before the clock starts.'
        },
        {
          kicker: 'Field',
          title: 'Offline capture',
          body: 'Records queue on device and sync when the signal returns.'
        },
        {
          kicker: 'Workflow',
          title: 'Leave and revision',
          body: 'Submissions route to approvers and land in Human Capital.'
        }
      ]
    },

    industries: {
      title: 'Built for Every Operation',
      items: [
        {
          name: 'Mining',
          body: 'Production, hauling, safety, and contractor control.',
          alt: 'Open-pit mine with haul trucks and an excavator at work'
        },
        {
          name: 'Construction',
          body: 'Project cost, manpower, and progress reporting.',
          alt: 'Engineers reviewing drawings on a construction site'
        },
        {
          name: 'Manufacturing',
          body: 'Plant output, downtime, and maintenance.',
          alt: 'Operator working at an automated production line'
        },
        {
          name: 'Logistics',
          body: 'Fleet, dispatch, and delivery visibility.',
          alt: 'Distribution centre with trucks queued at loading docks'
        },
        {
          name: 'Energy',
          body: 'Asset reliability and compliance across facilities.',
          alt: 'Technician monitoring equipment at a power plant facility'
        },
        {
          name: 'Enterprise Operations',
          body: 'Shared services standardized across entities.',
          alt: 'Corporate team meeting in a modern office'
        }
      ]
    },

    problems: {
      title: 'Every business solves problems every day.',
      body: 'Imagine if every solution you build could become the foundation for the next.'
    },

    intelligence: {
      kicker: 'Intelligence',
      title: ['From Enterprise Data', 'to Enterprise Intelligence.'],
      steps: [
        { name: 'Capture', body: 'Structured input from field, plant, and office.' },
        { name: 'Connect', body: 'One data model links every module.' },
        { name: 'Analyze', body: 'Cross-function metrics without exports.' },
        { name: 'Automate', body: 'Rules trigger the next operational step.' },
        { name: 'Predict', body: 'Early signals on cost, risk, and downtime.' }
      ]
    },

    technology: {
      title: 'Enterprise architecture, end to end.',
      lede: 'Web, mobile, API, workflow, data, analytics, and AI on one modular foundation.',
      layers: ['Web', 'Mobile', 'API', 'Workflow', 'Data', 'Analytics', 'AI'],
      capabilities: [
        'Cloud Ready',
        'API First',
        'Modular Architecture',
        'Role Based Access',
        'Multi Tenant',
        'Offline First',
        'Real-Time Sync',
        'Secure Enterprise Architecture',
        'Scalable Infrastructure'
      ]
    },

    whyCustom: {
      kicker: '// WHY CUSTOM SOFTWARE?',
      title: 'Why Build Custom Software Instead of Using Generic SaaS?',
      lede: [
        'Most people think software is just a website and a database. In reality, a professional enterprise system is a ',
        'complete digital infrastructure',
        ' built around your business processes.'
      ],
      comparisonAlt:
        'Two-panel illustration. Left, in daylight: a person imagines that building a system with "vibe coding" is only a front end and a back end — "just make the display, then the logic, done". Right, at night: a cutaway building showing the twelve teams actually at work — UI/UX design, front end, back end, security, database, API integration, DevOps and infrastructure, testing and QA, performance, monitoring and alerting, CI/CD pipeline, and documentation — resting on a foundation of network, OS, storage, backup, DNS, and SSL/TLS, with support and maintenance below.',
      comparisonCaption:
        'What people picture when a system is built with "vibe coding" — versus the twelve disciplines and the infrastructure that actually keep it running.',
      keyMessage: [
        'Custom software is not just code — it is a ',
        'complete digital ecosystem',
        ' designed specifically around your business.'
      ],
      benefits: [
        {
          title: 'Corporate Identity',
          body: "Your system reflects your company — not someone else's branding. Your workflow, your terminology, your business identity built into every screen."
        },
        {
          title: 'Faster Problem Solving',
          body: 'Need a feature? A workflow change? A new approval process? A custom system evolves immediately with your needs — no waiting for third-party vendors.'
        },
        {
          title: 'Unlimited Scalability',
          body: 'More employees, projects, clients, transactions — your system grows with you. No artificial plan limits, no per-seat pricing surprises.'
        },
        {
          title: 'Competitive Advantage',
          body: "Your competitors use the same SaaS. You operate with software specifically designed around your unique business processes — that's a moat."
        },
        {
          title: 'Better Integration',
          body: 'Connect ERP, Accounting, HR, Payroll, HSE, Approval, BI Dashboards, and E-Signature into one ecosystem — no duplicated work, no data silos.'
        },
        {
          title: 'Long-Term Cost Efficiency',
          body: 'Instead of paying subscriptions forever — you own the platform, control the roadmap, control the data, and control your future.'
        }
      ],
      difference: {
        kicker: '// THE OMASE DIFFERENCE',
        title: ["We Don't Just Build Software.", 'We Build Business Infrastructure.'],
        body: 'Every OMASE deployment is designed as a long-term business asset. From architecture, security, integrations, performance, HSE processes, HR management, approval workflows, KPI systems, document control, attendance tracking, and enterprise automation — everything is engineered to support sustainable business growth.'
      }
    },

    testimonials: {
      kicker: '// CLIENT VOICES',
      title: "Trusted by teams that can't afford downtime",
      items: [
        {
          quote:
            'OMASE rebuilt our entire HR and attendance flow into one platform. What used to take days of paperwork now happens in minutes — and it simply doesn’t go down.',
          role: 'Operations Director',
          sector: 'Mining & Resources'
        },
        {
          quote:
            'The HSE system gave our safety team real visibility for the first time. Compliance reporting that took weeks is now a live dashboard our board actually trusts.',
          role: 'HSE Manager',
          sector: 'Heavy Industry'
        },
        {
          quote:
            'They scaled the platform with us from one site to a dozen without a hitch. Professional, responsive, and genuinely invested as a long-term partner.',
          role: 'Project Lead',
          sector: 'Property Development'
        }
      ]
    },

    contact: {
      badge: 'Usually responds within 1 hour',
      title: "Let's Build Your Next Enterprise System",
      lede: 'Discuss your business challenges with our software engineering team and discover how digital transformation can improve efficiency, productivity, and growth.',
      emailLabel: 'Email',
      whatsappLabel: 'WhatsApp',
      whatsappTooltip: 'Need a consultation?',
      status: 'Online now — quick response guaranteed'
    },

    closing: {
      headline: ['Build Once.', 'Standardize Once.', 'Scale Everywhere.'],
      body: 'Transform your enterprise into one connected digital ecosystem with OMASE.',
      cta: 'Request a Demo'
    },

    demo: {
      eyebrow: 'Live demo',
      title: 'Try OMASE live',
      lede: 'Two ways in: the full admin console, or the mobile app crews use in the field.',
      options: [
        {
          label: 'Admin Panel',
          tagline: 'Full desktop console',
          body: 'Dashboards, workflow approvals, human capital, HSE, and analytics — the complete back office.',
          cta: 'Open admin demo'
        },
        {
          label: 'Mobile App',
          tagline: 'Installable PWA',
          body: 'Attendance with face verification, leave requests, and approvals from the gate or the pit.',
          cta: 'Open mobile demo'
        }
      ],
      credentialsLabel: 'Demo account',
      credentialLabels: { email: 'Email', password: 'Password' },
      note: 'Shared demo environment — data resets periodically, so treat it as a sandbox.',
      contactPrompt: 'Prefer a guided walkthrough?',
      contactCta: 'Request a call instead'
    },

    footer: {
      tagline: 'Omni Management System Enterprise',
      copyright: '© 2026 OMASE. One Platform. One Data. One Enterprise.',
      badges: 'Cloud Ready · API First · Offline First',
      builtByLabel: 'Built by',
      builtByTagline: 'Innovate · Transform · Elevate',
      city: 'Kota Tangerang, Banten, Indonesia',
      logoAlt: 'Baentech Solution logo'
    }
  }
}
